import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import type { StatusConexao } from '@/hooks/useSaudeConexoes';

export type SaudeStatus = 'healthy' | 'warning' | 'critical' | 'desconhecido';
export type PapelConexao = 'primaria' | 'secundaria' | 'reserva';

export interface ConexaoUsuario {
  papel: PapelConexao;
  id: string;
  nome: string;
  status: StatusConexao; // conectada | instavel | fora | verificando
  emUso: boolean;
}

export interface MinhaConexaoSaude {
  carregou: boolean;
  conexoes: ConexaoUsuario[];
  emUsoPapel: PapelConexao | null;
  primariaId: string | null;
  primariaInstanceName: string | null;
  primariaStatus: SaudeStatus;
  primariaReason: string | null;
  reservaId: string | null;
  reservaDown: boolean;
  caiu: boolean;
  semSaida: boolean;
}

function mapStatus(hs?: string | null, state?: string | null): StatusConexao {
  if (hs === 'critical') return 'fora';
  if (hs === 'warning') return 'instavel';
  // healthy com connection_state 'close' = zumbi (aceita envio no vacuo e renova a
  // propria "atividade") — mostra instavel, nunca "em uso". (08/07)
  if (hs === 'healthy') return (state ?? 'open') === 'open' ? 'conectada' : 'instavel';
  return 'verificando';
}

/**
 * Saude REAL das conexoes WhatsApp do usuario logado (primaria/secundaria/reserva).
 *
 * Le whatsapp_connection_health (watchdog, MESMA fonte do painel de Conexoes e do
 * useSaudeConexoes) — nao confia no connection_state='open' cru do Evolution, que
 * mente em sessao fantasma. Retorna as 3 conexoes com nome+status e qual esta EM USO
 * (a primeira da ordem primaria->secundaria->reserva que nao esta 'fora').
 */
export function useMinhaConexaoSaude(userId?: string | null): MinhaConexaoSaude {
  const { data } = useQuery({
    queryKey: ['minha-conexao-saude', userId],
    enabled: !!userId,
    refetchInterval: 60_000,
    staleTime: 30_000,
    queryFn: async () => {
      const { data: u } = await supabase
        .from('usuarios')
        .select('whatsapp_connection_id, whatsapp_connection_id_secundaria, equipe_id')
        .eq('id', userId as string)
        .maybeSingle();

      const primariaId = (u?.whatsapp_connection_id as string) ?? null;
      const secundariaId = (u?.whatsapp_connection_id_secundaria as string) ?? null;
      let reservaId: string | null = null;
      if (u?.equipe_id) {
        const { data: eq } = await supabase
          .from('equipes')
          .select('whatsapp_connection_id_reserva')
          .eq('id', u.equipe_id)
          .maybeSingle();
        reservaId = (eq?.whatsapp_connection_id_reserva as string) ?? null;
      }

      const ids = [primariaId, secundariaId, reservaId].filter(Boolean) as string[];
      let health: Array<{ connection_id: string; health_status: string; connection_state: string | null; reason: string | null }> = [];
      let nomes: Array<{ id: string; nome: string; provider_instance_id: string | null }> = [];
      if (ids.length > 0) {
        const [h, c] = await Promise.all([
          supabase.from('whatsapp_connection_health').select('connection_id, health_status, connection_state, reason').in('connection_id', ids),
          supabase.from('whatsapp_connections').select('id, nome, provider_instance_id').in('id', ids),
        ]);
        health = (h.data || []) as typeof health;
        nomes = (c.data || []) as typeof nomes;
      }
      return { primariaId, secundariaId, reservaId, health, nomes };
    },
  });

  const health = data?.health ?? [];
  const nomes = data?.nomes ?? [];
  const findH = (id?: string | null) => (id ? health.find((h) => h.connection_id === id) : undefined);
  const nomeDe = (id?: string | null) => (id ? (nomes.find((n) => n.id === id)?.nome ?? 'conexao') : '');
  const instDe = (id?: string | null) => (id ? (nomes.find((n) => n.id === id)?.provider_instance_id ?? null) : null);
  const statusDe = (id: string): StatusConexao => {
    const h = findH(id);
    return mapStatus(h?.health_status, h?.connection_state);
  };

  const brutas: Array<{ papel: PapelConexao; id: string | null }> = [
    { papel: 'primaria', id: data?.primariaId ?? null },
    { papel: 'secundaria', id: data?.secundariaId ?? null },
    { papel: 'reserva', id: data?.reservaId ?? null },
  ];
  const vistos = new Set<string>();
  const ordenadas = brutas
    .filter((b): b is { papel: PapelConexao; id: string } => !!b.id)
    .filter((b) => (vistos.has(b.id) ? false : (vistos.add(b.id), true)));

  // "Em uso agora" = primeira SAUDAVEL na ordem primaria -> secundaria -> reserva
  // (mesma regua do envio no backend). Antes bastava "nao critical": linha instavel
  // (warning, ex. state_close) seguia como "em uso" enquanto os envios ja saiam pela
  // reserva. Se nenhuma healthy, degrada pra primeira nao-caida. Quando a primaria
  // volta a healthy, o chip volta pra ela sozinho.
  const emUsoId =
    ordenadas.find((b) => statusDe(b.id) === 'conectada')?.id ??
    ordenadas.find((b) => statusDe(b.id) !== 'fora')?.id ??
    null;
  const conexoes: ConexaoUsuario[] = ordenadas.map((b) => ({
    papel: b.papel,
    id: b.id,
    nome: nomeDe(b.id),
    status: statusDe(b.id),
    emUso: b.id === emUsoId,
  }));
  const emUsoPapel = conexoes.find((c) => c.emUso)?.papel ?? null;

  const primariaStatus: SaudeStatus = (findH(data?.primariaId)?.health_status as SaudeStatus) ?? 'desconhecido';
  const caiu = primariaStatus === 'critical';
  const sec = findH(data?.secundariaId);
  const res = findH(data?.reservaId);
  const secOk = !!sec && sec.health_status !== 'critical';
  const resOk = !!res && res.health_status !== 'critical';
  const semSaida = caiu && !secOk && !resOk;

  return {
    carregou: !!data,
    conexoes,
    emUsoPapel,
    primariaId: data?.primariaId ?? null,
    primariaInstanceName: instDe(data?.primariaId),
    primariaStatus,
    primariaReason: findH(data?.primariaId)?.reason ?? null,
    reservaId: data?.reservaId ?? null,
    reservaDown: res?.health_status === 'critical',
    caiu,
    semSaida,
  };
}
